// Parses a pasted or CSV candidate list for Candidate Fit's "Import a list"
// option. One candidate per line. Works with or without a header row:
//
//   name,email,years,qualification            ← header (optional, any order)
//   Jane Doe,jane@x.com,6,BSc Economics
//   "Obi, Kelechi", k.obi@x.com, 3 yrs, MBA     ← quoted commas are fine
//   F. Balogun, 4 years, MBA                   ← no email (can't be sent a test)
//
// Without a header each field is recognised by its shape: an email address,
// a years figure ("6", "6 yrs", "6+ years"), and the first remaining field
// is the name; anything else is the qualification/notes.

const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[A-Za-z]{2,}$/;
const YEARS_RE = /^(\d{1,2})(?:\.\d+)?\s*\+?\s*(?:yrs?|years?)?(?:\s+(?:exp|experience))?$/i;

const HEADER_ALIASES = {
  name: ["name", "full name", "candidate", "candidate name"],
  email: ["email", "e-mail", "email address", "mail"],
  years: ["years", "yrs", "years experience", "years of experience", "experience", "exp", "experience (years)"],
  qualification: ["qualification", "qualifications", "degree", "education", "notes", "details", "skills"],
};

/**
 * Splits one CSV line, honouring double-quoted fields ("a, b" stays one
 * field). Empty cells are kept, so header columns stay aligned.
 */
export function splitCsvLine(line) {
  const out = [];
  let cur = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === "," || ch === ";" || ch === "\t") {
      out.push(cur.trim());
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out;
}

const headerKind = (cell) => {
  const c = cell.trim().toLowerCase();
  return Object.keys(HEADER_ALIASES).find((k) => HEADER_ALIASES[k].includes(c)) || null;
};

/** A header row: every cell is a known column name, and one of them is "name". */
function readHeader(cells) {
  const kinds = cells.map(headerKind);
  if (!kinds.length || kinds.some((k) => !k) || !kinds.includes("name")) return null;
  return kinds;
}

function toCandidate(fields) {
  const { name, email, years, rest } = fields;
  if (!name) return null;
  const cand = { name: name.slice(0, 120) };
  if (email && EMAIL_RE.test(email)) cand.email = email.toLowerCase();
  if (years !== undefined && years !== null && years !== "") {
    const m = String(years).match(/\d{1,2}/);
    if (m) cand.years_experience = Number(m[0]);
  }
  const qualification = rest.filter(Boolean).join(", ");
  if (qualification) cand.qualification = qualification;
  cand.meta = [cand.years_experience !== undefined ? `${cand.years_experience} yrs` : null, qualification || null]
    .filter(Boolean)
    .join(", ");
  return cand;
}

/**
 * Returns { candidates, skipped } — skipped are the line numbers (1-based)
 * that had no usable name.
 */
export function parseCandidateList(text) {
  const lines = String(text || "")
    .replace(/^﻿/, "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (!lines.length) return { candidates: [], skipped: [] };

  const header = readHeader(splitCsvLine(lines[0]).filter(Boolean));
  const body = header ? lines.slice(1) : lines;
  const offset = header ? 2 : 1;

  const candidates = [];
  const skipped = [];
  body.forEach((line, i) => {
    const cells = splitCsvLine(line);
    let fields;
    if (header) {
      fields = { rest: [] };
      cells.forEach((cell, idx) => {
        const kind = header[idx];
        if (kind === "name") fields.name = cell;
        else if (kind === "email") fields.email = cell;
        else if (kind === "years") fields.years = cell;
        else if (cell) fields.rest.push(cell);
      });
    } else {
      fields = { rest: [] };
      for (const cell of cells) {
        if (!cell) continue;
        if (!fields.email && EMAIL_RE.test(cell)) fields.email = cell;
        else if (fields.years === undefined && YEARS_RE.test(cell)) fields.years = cell;
        else if (!fields.name && !/\d{3,}/.test(cell)) fields.name = cell;
        else fields.rest.push(cell);
      }
    }
    const cand = toCandidate(fields);
    if (cand) candidates.push(cand);
    else skipped.push(i + offset);
  });

  // the same person twice in one paste: keep the last line
  const seen = new Map();
  for (const c of candidates) seen.set(c.email || `name:${c.name.toLowerCase()}`, c);
  return { candidates: [...seen.values()], skipped };
}
